import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Progress, Row, Col, Typography, Tooltip, Statistic } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  AlertOutlined,
  RiseOutlined,
  FallOutlined,
  DashboardOutlined,
  SafetyCertificateOutlined,
  DatabaseOutlined,
  DesktopOutlined,
} from '@ant-design/icons';
import { DataCard } from '../common';
import { useAggregatedSummary } from '../../hooks';

const { Text } = Typography;

// ============================================================================
// Styled Components
// ============================================================================

const WidgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const TopSection = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ScoreSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 140px;
`;

const ScoreLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
  text-align: center;
`;

const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ $trend }) => {
    if ($trend > 0) return 'var(--odin-success)';
    if ($trend < 0) return 'var(--odin-error)';
    return 'var(--odin-text-tertiary)';
  }};
  margin-top: 4px;
`;

const SummaryStats = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 3px solid ${({ $color, theme }) => $color || theme.colors.accent};
`;

const StatIcon = styled.span`
  font-size: 18px;
  color: ${({ $color, theme }) => $color || theme.colors.accent};
  display: flex;
  align-items: center;
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: 16px;
  line-height: 1.2;
`;

const StatLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const StatusSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing.lg};
`;

const StatusTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatusList = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  flex: 1;
  min-width: 120px;
`;

const StatusIcon = styled.span`
  font-size: 16px;
  color: ${({ $status, theme }) => {
    switch ($status) {
      case 'healthy': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      case 'critical': return theme.colors.error;
      default: return theme.colors.accent;
    }
  }};
`;

const StatusText = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const StatusCount = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get color based on health score
 * @param {number} score - Health score (0-100)
 * @returns {string} Semantic colour token
 */
const getScoreColor = (score) => {
  if (score >= 80) return 'var(--odin-success)'; // Healthy
  if (score >= 60) return 'var(--odin-warning)'; // Warning
  return 'var(--odin-error)'; // Critical
};

/**
 * Get status icon component based on status type
 * @param {string} status - Status type
 * @returns {JSX.Element} Icon component
 */
const getStatusIcon = (status) => {
  switch (status) {
    case 'healthy':
      return <CheckCircleOutlined />;
    case 'warning':
      return <WarningOutlined />;
    case 'critical':
      return <CloseCircleOutlined />;
    default:
      return <CheckCircleOutlined />;
  }
};

/**
 * Get trend icon based on trend value
 * @param {number} trend - Trend value (positive = up, negative = down)
 * @returns {JSX.Element|null} Trend icon or null
 */
const getTrendIcon = (trend) => {
  if (trend > 0) return <RiseOutlined />;
  if (trend < 0) return <FallOutlined />;
  return null;
};

/**
 * Format large numbers for display
 * @param {number} value - Number to format
 * @returns {string} Formatted string
 */
const formatNumber = (value) => {
  if (value === null || value === undefined) return '--';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

// ============================================================================
// Component
// ============================================================================

/**
 * HealthSummaryWidget - Aggregated dashboard overview widget
 *
 * Displays overall system health including:
 * - Health score (0-100) with trend indicator
 * - Total tenants count
 * - Total users, endpoints, and alerts
 * - Status breakdown (healthy/warning/critical)
 *
 * Supports two modes:
 * - useFetch=true: Fetches data from Gateway /aggregate/summary endpoint
 * - useFetch=false: Uses provided props (legacy mode for dashboard integration)
 *
 * @param {Object} props - Component props
 * @param {number} props.healthScore - Health score (0-100)
 * @param {Object} props.statusCounts - Status counts {healthy, warning, critical}
 * @param {Object} props.summary - Full summary data from Gateway
 * @param {Object} props.trends - Trend data {healthScore, tenants, users}
 * @param {boolean} props.loading - Loading state
 * @param {Object} props.error - Error object
 * @param {Function} props.onRefresh - Refresh callback
 * @param {boolean} props.useFetch - Whether to fetch data from Gateway (default: false)
 */
const HealthSummaryWidget = ({
  healthScore: propHealthScore = null,
  statusCounts: propStatusCounts = {},
  summary: propSummary = null,
  trends: propTrends = null,
  loading: propLoading = false,
  error: propError = null,
  onRefresh,
  useFetch = false,
}) => {
  // Fetch summary data from Gateway when useFetch is enabled
  const {
    data: fetchedData,
    isLoading: fetchLoading,
    error: fetchError,
    refetch,
  } = useAggregatedSummary({
    enabled: useFetch,
  });

  // Parse the summary response from Gateway
  const summaryData = useMemo(() => {
    if (!useFetch) {
      return propSummary;
    }

    // Gateway response format: { success: true, summary: {...} }
    if (fetchedData?.summary) {
      return fetchedData.summary;
    }

    return fetchedData;
  }, [useFetch, fetchedData, propSummary]);

  // Calculate derived values
  const metrics = useMemo(() => {
    const summary = summaryData || {};

    // Extract values from summary
    const totalTenants = summary.totalTenants ?? null;
    const totalUsers = summary.totalUsers ?? null;
    const totalEndpoints = summary.totalEndpoints ?? null;
    const totalThreats = summary.totalThreats ?? null;
    const totalOpenTickets = summary.totalOpenTickets ?? null;
    const tenantsWithIssues = summary.tenantsWithIssues ?? 0;
    const averageHealthScore = summary.averageHealthScore ?? propHealthScore;

    // Calculate status counts if not provided
    let statusCounts = { ...propStatusCounts };
    if (useFetch && totalTenants !== null) {
      // Calculate based on health thresholds
      const critical = tenantsWithIssues;
      const healthy = Math.max(0, totalTenants - critical);
      statusCounts = {
        healthy,
        warning: 0, // Gateway doesn't provide warning count, estimate if needed
        critical,
      };
    }

    return {
      healthScore: averageHealthScore,
      totalTenants,
      totalUsers,
      totalEndpoints,
      totalThreats,
      totalOpenTickets,
      tenantsWithIssues,
      statusCounts,
    };
  }, [summaryData, propHealthScore, propStatusCounts, useFetch]);

  // Determine loading and error states
  const isLoading = useFetch ? fetchLoading : propLoading;
  const error = useFetch ? fetchError : propError;

  // Refresh handler
  const handleRefresh = useCallback(() => {
    if (useFetch) {
      refetch();
    }
    onRefresh?.();
  }, [useFetch, refetch, onRefresh]);

  // Extract values for display
  const {
    healthScore,
    totalTenants,
    totalUsers,
    totalEndpoints,
    totalThreats,
    statusCounts,
  } = metrics;

  const { healthy = 0, warning = 0, critical = 0 } = statusCounts;

  // Get trends (if available)
  const trends = propTrends || {};

  return (
    <DataCard
      title="Overall Health Summary"
      icon={DashboardOutlined}
      loading={isLoading}
      error={error}
      onRefresh={handleRefresh}
      minHeight="220px"
    >
      <WidgetContainer>
        {/* Top Section: Health Score + Summary Stats */}
        <TopSection>
          {/* Health Score Circle */}
          <ScoreSection>
            <Progress
              type="circle"
              percent={healthScore ?? 0}
              strokeColor={getScoreColor(healthScore ?? 0)}
              trailColor="var(--odin-border)"
              format={(percent) => (
                <span style={{ color: 'var(--odin-text-primary)', fontSize: 28, fontWeight: 700 }}>
                  {healthScore !== null ? percent : '--'}
                </span>
              )}
              width={120}
            />
            <ScoreLabel>Health Score</ScoreLabel>
            {trends.healthScore !== undefined && trends.healthScore !== 0 && (
              <TrendIndicator $trend={trends.healthScore}>
                {getTrendIcon(trends.healthScore)}
                <span>{trends.healthScore > 0 ? '+' : ''}{trends.healthScore}%</span>
              </TrendIndicator>
            )}
          </ScoreSection>

          {/* Summary Statistics Grid */}
          <SummaryStats>
            <Tooltip title="Total tenants managed across all services">
              <StatItem $color="var(--odin-accent)">
                <StatIcon $color="var(--odin-accent)">
                  <TeamOutlined />
                </StatIcon>
                <StatContent>
                  <StatValue>{formatNumber(totalTenants)}</StatValue>
                  <StatLabel>Tenants</StatLabel>
                </StatContent>
              </StatItem>
            </Tooltip>

            <Tooltip title="Total users across all tenants">
              <StatItem $color="var(--odin-text-secondary)">
                <StatIcon $color="var(--odin-text-secondary)">
                  <SafetyCertificateOutlined />
                </StatIcon>
                <StatContent>
                  <StatValue>{formatNumber(totalUsers)}</StatValue>
                  <StatLabel>Users</StatLabel>
                </StatContent>
              </StatItem>
            </Tooltip>

            <Tooltip title="Protected endpoints across all tenants">
              <StatItem $color="var(--odin-status-active-text)">
                <StatIcon $color="var(--odin-status-active-text)">
                  <DesktopOutlined />
                </StatIcon>
                <StatContent>
                  <StatValue>{formatNumber(totalEndpoints)}</StatValue>
                  <StatLabel>Endpoints</StatLabel>
                </StatContent>
              </StatItem>
            </Tooltip>

            <Tooltip title="Active threats and alerts requiring attention">
              <StatItem $color={totalThreats > 0 ? 'var(--odin-error)' : 'var(--odin-success)'}>
                <StatIcon $color={totalThreats > 0 ? 'var(--odin-error)' : 'var(--odin-success)'}>
                  <AlertOutlined />
                </StatIcon>
                <StatContent>
                  <StatValue>{formatNumber(totalThreats)}</StatValue>
                  <StatLabel>Alerts</StatLabel>
                </StatContent>
              </StatItem>
            </Tooltip>
          </SummaryStats>
        </TopSection>

        {/* Status Breakdown Section */}
        <StatusSection>
          <StatusTitle>Tenant Health Status</StatusTitle>
          <StatusList>
            <StatusItem>
              <StatusIcon $status="healthy">
                {getStatusIcon('healthy')}
              </StatusIcon>
              <StatusText>Healthy</StatusText>
              <StatusCount>{healthy}</StatusCount>
            </StatusItem>
            <StatusItem>
              <StatusIcon $status="warning">
                {getStatusIcon('warning')}
              </StatusIcon>
              <StatusText>Warning</StatusText>
              <StatusCount>{warning}</StatusCount>
            </StatusItem>
            <StatusItem>
              <StatusIcon $status="critical">
                {getStatusIcon('critical')}
              </StatusIcon>
              <StatusText>Critical</StatusText>
              <StatusCount>{critical}</StatusCount>
            </StatusItem>
          </StatusList>
        </StatusSection>
      </WidgetContainer>
    </DataCard>
  );
};

HealthSummaryWidget.propTypes = {
  /** Health score (0-100) */
  healthScore: PropTypes.number,
  /** Status counts by category */
  statusCounts: PropTypes.shape({
    healthy: PropTypes.number,
    warning: PropTypes.number,
    critical: PropTypes.number,
  }),
  /** Full summary data from Gateway */
  summary: PropTypes.shape({
    totalTenants: PropTypes.number,
    totalUsers: PropTypes.number,
    totalEndpoints: PropTypes.number,
    totalThreats: PropTypes.number,
    totalOpenTickets: PropTypes.number,
    tenantsWithIssues: PropTypes.number,
    averageHealthScore: PropTypes.number,
  }),
  /** Trend data for displaying changes */
  trends: PropTypes.shape({
    healthScore: PropTypes.number,
    tenants: PropTypes.number,
    users: PropTypes.number,
  }),
  /** Loading state (used when useFetch=false) */
  loading: PropTypes.bool,
  /** Error object (used when useFetch=false) */
  error: PropTypes.object,
  /** Callback when refresh is requested */
  onRefresh: PropTypes.func,
  /** Whether to fetch data from Gateway /aggregate/summary (default: false) */
  useFetch: PropTypes.bool,
};

export default HealthSummaryWidget;
